import {inject, Injectable, signal} from '@angular/core';
import {
	MergeReceivedDTO,
	Ticket,
	TicketAssigneeRequest,
	TicketDashboardStat
} from '../models/ticket.model';
import {TicketEmailVM} from '../components/emails/emails.model';
import {TicketEvent} from '../models/ticket-event.model';
import {ToastMessageEffectService} from '../../../core/services/toast-message-effect.service';

@Injectable({ providedIn: 'root' })
export class TicketsStore {
	private toastEffect = inject(ToastMessageEffectService);

	private readonly _tickets = signal<Ticket[]>([])
	private readonly _dashboardTickets = signal<Ticket[]>([])
	private readonly _dashboardStats = signal<TicketDashboardStat[]>([])
	private readonly _assigneeRequests = signal<TicketAssigneeRequest[]>([])
	private readonly _targetMergeTickets = signal<MergeReceivedDTO[]>([])
	private readonly _mergeMap = signal<Map<string, string[]>>(new Map())

	private readonly _loading = signal(false)
	private readonly _dashboardTicketsLoading = signal(false)
	private readonly _dashboardCategorizationLoading = signal(false)
	private readonly _dashboardStatsLoading = signal(false)
	private readonly _error = signal<string | null>(null)

	readonly tickets = this._tickets.asReadonly()
	readonly dashboardTickets = this._dashboardTickets.asReadonly()
	readonly dashboardStats = this._dashboardStats.asReadonly()
	readonly assigneeRequests = this._assigneeRequests.asReadonly()
	readonly targetMergeTickets = this._targetMergeTickets.asReadonly()
	readonly mergeMap = this._mergeMap.asReadonly()
	readonly loading = this._loading.asReadonly()
	readonly dashboardTicketsLoading = this._dashboardTicketsLoading.asReadonly()
	readonly dashboardCategorizationLoading = this._dashboardCategorizationLoading.asReadonly()
	readonly dashboardStatsLoading = this._dashboardStatsLoading.asReadonly()
	readonly error = this._error.asReadonly()

	constructor() {
		this.toastEffect.registerErrorSignal(this.error);
	}

	setTickets(tickets: Ticket[]) {
		this._tickets.set(tickets);
	}

	setDashboardTickets(tickets: Ticket[]) {
		this._dashboardTickets.set(tickets);
	}

	setDashboardCategorizationTickets(tickets: Ticket[]) {
		this._dashboardTickets.update(list => [
			...list.filter(t => t.category !== 'NOT_CATEGORIZED'),
			...tickets
		]);
	}

	setDashboardStats(stats: TicketDashboardStat[]) {
		this._dashboardStats.set(stats);
	}

	setAssigneeRequests(requests: TicketAssigneeRequest[]) {
		this._assigneeRequests.set(requests);
	}

	setMergeTargets(data: MergeReceivedDTO[]) {
		const map = new Map<string, string[]>();

		for (const item of data) {
			map.set(item.ticket_id, item.merged_ticket_ids);
		}

		this._mergeMap.set(map);
	}

	updateTicket(patch: Partial<Ticket> & { id: string }) {
		this._tickets.update(list =>
			list.map(t =>
				t.id === patch.id
					? { ...t, ...patch }
					: t
			)
		);

		this._dashboardTickets.update(list =>
			list.map(t =>
				t.id === patch.id
					? { ...t, ...patch }
					: t
			)
		);
	}

	setLoading(val: boolean) {
		this._loading.set(val);
	}

	setDashboardTicketsLoading(val: boolean) {
		this._dashboardTicketsLoading.set(val);
	}

	setDashboardCategorizationLoading(val: boolean) {
		this._dashboardCategorizationLoading.set(val);
	}

	setDashboardStatsLoading(val: boolean) {
		this._dashboardStatsLoading.set(val);
	}

	setError(err: string | null) {
		this._error.set(null);
		this._error.set(err);
	}

	setTicketEmails(ticketId: string, emails: TicketEmailVM[]) {
		this.updateTicket({
			id: ticketId,
			emails,
			emailsLoaded: true,
			emailsLoading: false
		});
	}

	setEmailsLoading(ticketId: string, loading: boolean) {
		this.updateTicket({
			id: ticketId,
			emailsLoading: loading
		});
	}

	setTicketEvents(ticketId: string, events: TicketEvent[]) {
		this.updateTicket({
			id: ticketId,
			events,
			eventsLoaded: true,
			eventsLoading: false
		});
	}

	setEventsLoading(ticketId: string, loading: boolean) {
		this._tickets.update(list =>
			list.map(t =>
				t.id === ticketId
					? { ...t, eventsLoading: loading }
					: t
			)
		);
	}

	getTicketById(id: string): Ticket | undefined {
		return this._tickets().find(t => t.id === id)
			?? this._dashboardTickets().find(t => t.id === id);
	}

}
